import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const CartCtx = createContext(null);
const CART_ID_KEY = "atelier_cart_id";
const CART_TOKEN_KEY = "atelier_cart_access_token";

export function getCartHeaders() {
  const token = localStorage.getItem(CART_TOKEN_KEY);
  return token ? { "X-Cart-Token": token } : {};
}

export function getCartAccessToken() {
  return localStorage.getItem(CART_TOKEN_KEY);
}

export function CartProvider({ children }) {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);
  // A brief "added to cart" announcement. The card's own checkmark was too
  // quiet — shoppers pressed a size, saw nothing obvious, and thought it had
  // not worked. This drives a page-level toast that says what went in.
  const [toast, setToast] = useState(null);
  const dismissToast = () => setToast(null);

  const ensure = useCallback(async () => {
    let id = localStorage.getItem(CART_ID_KEY);
    if (id) {
      try {
        const r = await api.get(`/carts/${id}`, { headers: getCartHeaders() });
        if (r.data.status === "active") { setCart(r.data); return r.data; }
      } catch (err) {
        // Stale cart id — fall through to create a new one.
        console.warn("[atelier] cart", id, "not fetchable, recreating", err?.response?.status);
        localStorage.removeItem(CART_ID_KEY);
        localStorage.removeItem(CART_TOKEN_KEY);
      }
    }
    const r = await api.post("/carts", {});
    localStorage.setItem(CART_ID_KEY, r.data.id);
    if (r.data.cart_access_token) localStorage.setItem(CART_TOKEN_KEY, r.data.cart_access_token);
    setCart(r.data);
    return r.data;
  }, []);

  useEffect(() => {
    ensure().catch((err) => console.error("[atelier] cart bootstrap failed", err));
  }, [ensure]);

  /* A tab left open across a deploy, or a second tab, holds a cart the server
     has since moved on from — deletions made elsewhere never show up in it.
     Reconcile with the server whenever the tab comes back into view. */
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState !== "visible") return;
      const id = localStorage.getItem(CART_ID_KEY);
      if (!id) return;
      api.get(`/carts/${id}`, { headers: getCartHeaders() })
        .then((r) => { if (r.data.status === "active") setCart(r.data); })
        .catch(() => {});
    };
    window.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      window.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, []);

  /* `gift` carries who a gift card is for. The server ignores it on any line
     that is not a gift card, so a caller cannot attach a recipient to a jacket. */
  const addItem = async (variant_id, quantity = 1, gift = undefined) => {
    setLoading(true);
    try {
      const body = { variant_id, quantity };
      if (gift && Object.values(gift).some(Boolean)) body.gift = gift;
      const post = (cartId) => api.post(`/carts/${cartId}/items`, body, { headers: getCartHeaders() });
      const applied = (data) => {
        setCart(data);
        const line = (data.items || []).find((it) => it.variant_id === variant_id);
        setToast({
          at: Date.now(),
          product_title: line?.product_title || null,
          variant_title: line?.variant_title || null,
        });
        return data;
      };
      const c = cart || (await ensure());
      try {
        const r = await post(c.id);
        return applied(r.data);
      } catch (err) {
        // The stored cart was consumed by an order (it goes non-active the
        // moment checkout completes → 409 "Cart is not active"), lost its access
        // token across a deploy (403), or was swept away (404). All three read
        // to the shopper as a bare "əməliyyat tamamlanmadı". Drop the dead cart,
        // open a fresh one, and add the piece once more so the click simply
        // works. A genuine 409 like "Insufficient stock" is NOT a dead cart —
        // it must surface, so match the identity messages, not the bare code.
        const st = err?.response?.status;
        const detail = err?.response?.data?.detail;
        const deadCart = st === 403 || st === 404
          || (typeof detail === "string" && /not active|Cart not found|Cart access denied/i.test(detail));
        if (!deadCart) throw err;
        localStorage.removeItem(CART_ID_KEY);
        localStorage.removeItem(CART_TOKEN_KEY);
        setCart(null);
        const fresh = await ensure();
        const r = await post(fresh.id);
        return applied(r.data);
      }
    } finally { setLoading(false); }
  };

  const addLook = async (product_handle, variants) => {
    setLoading(true);
    try {
      const c = cart || (await ensure());
      const r = await api.post(
        `/carts/${c.id}/add-look`,
        { product_handle, variants },
        { headers: getCartHeaders() }
      );
      setCart(r.data);
      return r.data;
    } finally { setLoading(false); }
  };

  const patchItem = async (item_id, quantity) => {
    if (!cart) return;
    const r = await api.patch(`/carts/${cart.id}/items/${item_id}`, { quantity }, { headers: getCartHeaders() });
    setCart(r.data);
    return r.data;
  };

  const removeItem = async (item_id) => {
    if (!cart) return;
    const r = await api.delete(`/carts/${cart.id}/items/${item_id}`, { headers: getCartHeaders() });
    setCart(r.data);
  };

  const applyPromotion = async (code) => {
    const c = cart || (await ensure());
    const r = await api.post(`/carts/${c.id}/promotions/apply`, { code }, { headers: getCartHeaders() });
    setCart(r.data);
    return r.data;
  };

  const removePromotion = async (code) => {
    if (!cart) return;
    const r = await api.delete(`/carts/${cart.id}/promotions/${encodeURIComponent(code)}`, { headers: getCartHeaders() });
    setCart(r.data);
    return r.data;
  };

  /* A gift card is tender, not a discount, so it has its own pair of actions
     rather than riding on the promotion endpoints: applying an empty or unpaid
     card should say so, not silently add nothing to the total. */
  const applyGiftCard = async (code) => {
    const c = cart || (await ensure());
    const r = await api.post(`/carts/${c.id}/gift-cards`, { code }, { headers: getCartHeaders() });
    setCart(r.data);
    return r.data;
  };

  const removeGiftCard = async (code) => {
    if (!cart) return;
    const r = await api.delete(`/carts/${cart.id}/gift-cards/${encodeURIComponent(code)}`, { headers: getCartHeaders() });
    setCart(r.data);
    return r.data;
  };

  /* Delivery is priced server-side, so choosing a method has to round-trip:
     the fee lands in `shipping_total` and the returned `total` is what the
     order will actually charge. */
  const setDeliveryMethod = async (code) => {
    if (!cart || !code) return null;
    const r = await api.put(`/carts/${cart.id}/delivery-method`, { code }, { headers: getCartHeaders() });
    setCart(r.data);
    return r.data;
  };

  const complete = async (payload) => {
    if (!cart) throw new Error("No cart");
    const r = await api.post(`/carts/${cart.id}/complete`, payload, { headers: getCartHeaders() });
    localStorage.removeItem(CART_ID_KEY);
    localStorage.removeItem(CART_TOKEN_KEY);
    setCart(null);
    // ensure a fresh empty cart is available for the next visit
    setTimeout(() => {
      ensure().catch((err) => console.error("[atelier] post-checkout cart re-init failed", err));
    }, 100);
    return r.data;
  };

  const adoptCart = async (cartId) => {
    if (!cartId) return null;
    localStorage.setItem(CART_ID_KEY, cartId);
    localStorage.removeItem(CART_TOKEN_KEY);
    const r = await api.get(`/carts/${cartId}`);
    setCart(r.data);
    return r.data;
  };

  // After a failed card payment the order holds the pieces; this rebuilds a
  // fresh basket from it (the server cancels the dead order) so the shopper
  // lands back on their basket instead of an empty one. Keeps the guest token.
  const restoreCartFromOrder = async (orderId) => {
    const r = await api.post(`/carts/from-order/${orderId}`, {});
    const data = r.data;
    localStorage.setItem(CART_ID_KEY, data.id);
    if (data.cart_access_token) localStorage.setItem(CART_TOKEN_KEY, data.cart_access_token);
    else localStorage.removeItem(CART_TOKEN_KEY);
    setCart(data);
    return data;
  };

  const itemCount = (cart?.items || []).reduce((n, it) => n + it.quantity, 0);

  return (
    <CartCtx.Provider value={{
      cart,
      loading,
      addItem,
      addLook,
      patchItem,
      removeItem,
      applyPromotion,
      removePromotion,
      applyGiftCard,
      removeGiftCard,
      setDeliveryMethod,
      complete,
      adoptCart,
      restoreCartFromOrder,
      itemCount,
      toast,
      dismissToast,
      reload: ensure,
    }}>
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
